import { cleanIntent } from '../components/AddToHelpers';

/**
 * Generic function to process list items and fetch missing data based on a configurable filter
 * @param {Object} params - Parameters object
 * @param {Object} params.selectedListItems - Object containing selected list items
 * @param {Map} params.itemDataMap - Map containing cached data
 * @param {Function} params.fetchListProducts - Function to fetch product data
 * @param {Function} params.updateFunction - Generic function to update data
 * @param {string} params.storeId - Store ID for the request
 * @param {string} params.storeZip - Store ZIP code for the request
 * @param {Function} params.filterCondition - Function that returns true if item needs data fetching
 * @param {number} params.batchSizeLimit - Maximum number of items to process in one batch
 */
export const processListItemsForDataUpdate = ({
  selectedListItems,
  itemDataMap,
  fetchListProducts,
  updateFunction,
  storeId,
  storeZip,
  filterCondition,
  batchSizeLimit = 16
}) => {
  const listItems = Object.values(selectedListItems);
  const listItemsWithMissingData = listItems.filter(
    (items) => items.some(filterCondition)
  );

  if (listItemsWithMissingData.length > 0) {
    const firstListItems = listItemsWithMissingData[0]?.filter(filterCondition);
    const itemIds = firstListItems.slice(0, batchSizeLimit)?.map((item) => item.itemId);
    const itemsKey = itemIds.join(':');

    if (itemDataMap.has(itemsKey)) {
      updateFunction(itemDataMap.get(itemsKey));
    } else {
      fetchListProducts({
        variables: {
          itemIds,
          storeId,
          zipCode: storeZip
        }
      });
    }
  }
};

/**
 * Generic function to process product data response and update cached data
 * @param {Object} params - Parameters object
 * @param {Array} params.itemIds - Array of item IDs from the request variables
 * @param {Array} params.products - Array of product data from the response
 * @param {Function} params.dataExtractor - Function to extract the desired data from each product
 * @param {Function} params.updateFunction - Function to update the data (e.g., updateBasePrice)
 * @param {Function} params.setDataMap - Setter function for the data map state
 * @param {boolean} params.error - Whether there was an error in the request
 */
export const processProductDataResponse = ({
  itemIds,
  products = [],
  dataExtractor,
  updateFunction,
  setDataMap,
  error = false
}) => {
  if (itemIds && (products.length > 0 || Boolean(error))) {
    const productsMap = new Map();
    products.forEach((product) => productsMap.set(product.itemId, product));

    const itemDataMap = new Map();
    itemIds.forEach((itemId) => {
      const product = productsMap.get(itemId);
      const extractedData = dataExtractor(product);
      itemDataMap.set(itemId, extractedData);
    });

    updateFunction(itemDataMap);
    setDataMap((prevMap) => {
      const newMap = new Map(prevMap);
      const itemsKey = itemIds.join(':');
      newMap.set(itemsKey, itemDataMap);
      return newMap;
    });
  }
};

/**
 * Consolidates duplicate items from selected list items by itemId and sums their quantities
 * @param {Object} selectedListItems - Object containing selected list items from different lists
 * @returns {Array} Array of consolidated items with unique itemIds and summed quantities
 */
export const consolidateSelectedItems = (selectedListItems) => {
  const selectedProductsToAdd = Object.values(selectedListItems).flat();

  if (selectedProductsToAdd?.length === 0) {
    return [];
  }

  const itemMap = new Map();
  selectedProductsToAdd.forEach((item) => {
    if (itemMap.has(item.itemId)) {
      const existingItem = itemMap.get(item.itemId);
      const modifiedItem = { ...existingItem, quantity: existingItem.quantity + item.quantity };
      itemMap.set(item.itemId, modifiedItem);
    } else {
      itemMap.set(item.itemId, item);
    }
  });

  return Array.from(itemMap.values());
};

/**
 * Organizes consolidated items into groups for the listOperations mutation.
 * Returns separate arrays for default (ungrouped) items and named groups,
 * since the backend requires different operations:
 * - 'AddItems' for default/ungrouped items (null groupId)
 * - 'Create' for named groups (groupName-based matching/creation)
 * @param {Array} items - Consolidated items with optional groupName property
 * @returns {{ defaultGroups: Array, namedGroups: Array }} Split group arrays for listOperations
 */
export const organizeItemsByGroup = (items) => {
  if (!items || items.length === 0) return { defaultGroups: [], namedGroups: [] };

  const groupMap = new Map();

  items.forEach((item) => {
    const key = item.groupName || '__default__';
    if (!groupMap.has(key)) {
      groupMap.set(key, []);
    }
    const product = {
      itemId: item.itemId,
      quantity: item.quantity,
    };
    if (item.intent) {
      product.intent = cleanIntent(item.intent);
    }
    if (item.comments !== undefined) {
      product.comments = item.comments;
    }
    // PRO41-1848 (repair round 2): do NOT forward the SOURCE item's intentId on copy — a
    // copy creates a brand-new item in the destination list, and the destination backend
    // (mylist-api-gql) hard-rejects a foreign/unrecognized intentId as invalid
    // (MYLIST_ERR_156), so the item silently fails server-side. Resolution state alone
    // (intentResolved) is the correct thing to carry over; the backend mints its own
    // intentId for the new item.
    if (item.intentResolved !== undefined) {
      product.intentResolved = item.intentResolved;
    }
    groupMap.get(key).push(product);
  });

  const defaultGroups = [];
  const namedGroups = [];

  groupMap.forEach((products, key) => {
    if (key === '__default__') {
      defaultGroups.push({ groupId: null, products });
    } else {
      namedGroups.push({ groupName: key, products });
    }
  });

  return { defaultGroups, namedGroups };
};

/**
 * Updates selected list items with new data based on a configurable property and condition
 * @param {Function} setSelectedListItems - State setter function for selectedListItems
 * @param {Map} dataMap - Map containing the new data keyed by itemId
 * @param {string} propertyName - Name of the property to update (e.g., 'basePrice', 'sku')
 * @param {*} notFetchedValue - Value that indicates data hasn't been fetched yet
 * @returns {Function} Function that can be used as an update handler
 */
const applyPropertyUpdate = (prevItems, dataMap, propertyName, notFetchedValue) => {
  let hasChanges = false;
  const updatedSelectedListItems = {};
  Object.keys(prevItems).forEach((prevItemListId) => {
    updatedSelectedListItems[prevItemListId] = prevItems[prevItemListId]?.map((item) => {
      if (item[propertyName] === notFetchedValue) {
        const newValue = dataMap.get(item.itemId) || notFetchedValue;
        if (newValue !== notFetchedValue) {
          hasChanges = true;
          return { ...item, [propertyName]: newValue };
        }
      }
      return item;
    });
  });
  return hasChanges ? updatedSelectedListItems : prevItems;
};

export const updateSelectedListItemsProperty = (setSelectedListItems, dataMap, propertyName, notFetchedValue) => {
  return () => {
    setSelectedListItems((prevItems) => applyPropertyUpdate(prevItems, dataMap, propertyName, notFetchedValue));
  };
};
